/**
 * Project catalog + bundles on Firebase.
 * Firestore: projects/{projectId} — sidebar metadata (no full structure; 1MB limit)
 * Storage:   projects/{projectId}/project.json — full local project.json
 *            projects/{projectId}/bible.json — optional bible snapshot
 *
 * Local Express disk remains the write path when the Thor API is up.
 * Prod hosting reads this catalog when /api is unreachable.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import {
  getBlob,
  getBytes,
  getDownloadURL,
  ref,
  uploadBytes,
  uploadString,
} from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { writesStayLocal } from './localOnly';

function requireAuth() {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('Sign in required to sync projects to Firebase');
    err.code = 'AUTH_REQUIRED';
    throw err;
  }
  return user;
}

/** Slim fields safe for Firestore project docs (sidebar / archive lists). */
export function projectCloudMeta(project, extras = {}) {
  if (!project?.id) return null;
  return {
    id: project.id,
    title: project.title || project.id,
    slug: project.slug || project.id,
    status: project.status || 'active',
    bibleVersion: project.bibleVersion || null,
    biblePath: project.biblePath || null,
    sectionOrder: project.sectionOrder || null,
    organizationModel: project.organizationModel || null,
    resolution: project.resolution || null,
    notes: typeof project.notes === 'string' ? project.notes.slice(0, 2000) : null,
    createdAt: project.createdAt || null,
    updatedAt: project.updatedAt || new Date().toISOString(),
    archivedAt: project.archivedAt || null,
    hasStorageBundle: true,
    ...extras,
  };
}

export async function upsertCloudProjectMeta(project, extras = {}) {
  if (writesStayLocal()) return null;
  requireAuth();
  const meta = projectCloudMeta(project, {
    ...extras,
    syncedAt: new Date().toISOString(),
    syncedBy: auth.currentUser.email || null,
  });
  if (!meta) return null;
  await setDoc(doc(db, 'projects', meta.id), meta, { merge: true });
  return meta;
}

/** Upload full project.json (+ optional bible) to Storage and refresh Firestore meta. */
export async function persistProjectBundleToCloud(projectId, { project, bible = null } = {}) {
  if (writesStayLocal()) return null;
  requireAuth();
  if (!projectId || !project) return null;

  const projectBytes = new TextEncoder().encode(`${JSON.stringify(project)}\n`);
  await uploadBytes(ref(storage, `projects/${projectId}/project.json`), projectBytes, {
    contentType: 'application/json',
    customMetadata: {
      projectId,
      updatedAt: project.updatedAt || new Date().toISOString(),
    },
  });

  if (bible && typeof bible === 'object') {
    const bibleBytes = new TextEncoder().encode(`${JSON.stringify(bible)}\n`);
    await uploadBytes(ref(storage, `projects/${projectId}/bible.json`), bibleBytes, {
      contentType: 'application/json',
    });
  }

  const chapterCount = (project.structure?.chapters || []).length;
  const characterCount = (project.structure?.characters || []).length;
  const otherCount = (project.structure?.other || []).length;
  const pageCount = (project.structure?.chapters || []).reduce(
    (n, ch) => n + (ch.pages || []).length,
    0
  );

  return upsertCloudProjectMeta(project, {
    stats: { chapters: chapterCount, characters: characterCount, other: otherCount, pages: pageCount },
  });
}

export async function listCloudProjects({ includeArchived = false } = {}) {
  requireAuth();
  const snap = await getDocs(collection(db, 'projects'));
  let projects = snap.docs.map((d) => {
    const data = d.data() || {};
    return {
      id: d.id,
      title: data.title || d.id,
      slug: data.slug || d.id,
      status: data.status || 'active',
      bibleVersion: data.bibleVersion || null,
      sectionOrder: data.sectionOrder || null,
      organizationModel: data.organizationModel || null,
      resolution: data.resolution || null,
      updatedAt: data.updatedAt || null,
      createdAt: data.createdAt || null,
      archivedAt: data.archivedAt || null,
      stats: data.stats || null,
      hasStorageBundle: Boolean(data.hasStorageBundle),
      source: 'firebase',
    };
  });
  if (!includeArchived) {
    projects = projects.filter((p) => (p.status || 'active') !== 'archived');
  } else {
    projects = projects.filter((p) => p.status === 'archived');
  }
  projects.sort((a, b) => String(a.title).localeCompare(String(b.title)));
  return projects;
}

const MAX_BUNDLE_BYTES = 25 * 1024 * 1024;

/**
 * Prefer tokenized download URL + fetch (reliable CORS on artificiallly.com).
 * Fall back to getBytes with an explicit size cap.
 */
async function readJsonFromStorage(storagePath) {
  const objectRef = ref(storage, storagePath);
  try {
    const url = await getDownloadURL(objectRef);
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      throw new Error(`Storage download failed (${res.status}) for ${storagePath}`);
    }
    return await res.json();
  } catch (primary) {
    try {
      const bytes = await getBytes(objectRef, MAX_BUNDLE_BYTES);
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (secondary) {
      const err = new Error(primary?.message || secondary?.message || 'Storage read failed');
      err.cause = secondary || primary;
      throw err;
    }
  }
}

/** Tokenized URL an image tag can load without the generation API. */
export async function downloadUrlForStoragePath(storagePath) {
  if (!storagePath) return '';
  try {
    return await getDownloadURL(ref(storage, storagePath));
  } catch {
    return '';
  }
}

/** Save a Storage object into the browser Downloads folder. */
export async function downloadStorageFile(storagePath, filename) {
  if (!storagePath) {
    throw new Error('300dpi file is not in Storage yet');
  }
  const blob = await getBlob(ref(storage, storagePath));
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename || storagePath.split('/').pop() || 'export-300dpi.png';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

/** Load full project bundle from Storage (prod path when local API is down). */
export async function getCloudProjectBundle(projectId) {
  requireAuth();
  const metaSnap = await getDoc(doc(db, 'projects', projectId));
  if (!metaSnap.exists()) {
    const err = new Error(`Project not found in Firebase: ${projectId}`);
    err.status = 404;
    throw err;
  }

  let project;
  try {
    project = await readJsonFromStorage(`projects/${projectId}/project.json`);
  } catch (e) {
    const err = new Error(
      `Project metadata exists but Storage bundle is missing for ${projectId}. Re-open the project on Thor to sync.`
    );
    err.status = 404;
    err.cause = e;
    throw err;
  }

  let bible = null;
  try {
    bible = await readJsonFromStorage(`projects/${projectId}/bible.json`);
  } catch {
    bible = null;
  }

  return {
    project,
    bible,
    structure: project.structure || null,
    resolution: project.resolution || null,
    pages: [],
    source: 'firebase',
  };
}

/** Best-effort: write an empty marker doc (used by older sync paths). */
export async function ensureCloudProjectDoc(projectId, partial = {}) {
  if (writesStayLocal()) return null;
  requireAuth();
  await setDoc(
    doc(db, 'projects', projectId),
    {
      id: projectId,
      updatedAt: new Date().toISOString(),
      syncedBy: auth.currentUser.email || null,
      ...partial,
    },
    { merge: true }
  );
}

// uploadString kept available for tiny text helpers
void uploadString;
